/* AddEditUserRoleRenderer.js */

"use strict";
/* global Slick */

const _ = require("lodash");
const uiCore = require("@gluenetworks/steve_ui_core");
const formatter = require("../../cellFormaters/index");
const checkBoxEditor = require("./cellEditors/CheckBoxEditor");
const InputTextEditor = require("./cellEditors/InputTextEditor");
const CheckboxControl = uiCore.Control.Boolean.InputCheckbox;
const ButtonControl = uiCore.Control.Action.Button;
const ComboBoxControl = uiCore.Control.OptionSelection.Single.ComboBox;
const InputTextControl = uiCore.Control.Textfield.InputText;
const AdvancedFilter = require("@gluenetworks/entity_grid_gl").AdvancedFilter;
const MultiCombobox = require("../../../../../uiutils/orgComboBoxComponent/OrgsComboBoxComponent");
const InputRadioControl = uiCore.Control.OptionSelection.Single.InputRadio;

const MODAL_TITLE_ADD = "Define Role";

class AddEditUserRoleRenderer {
    constructor(model) {
        this._model = model;
        this._advancedFilter = undefined;
        this._advancedFilterReadonly = undefined;
        this._readOnlyFilterValue = null;
        this._readOnlyOrgSelections = null;
        this._filterValue = null;
        this._filterMatchContainer = undefined;
        this._filterMatchParent = undefined;

        this._fitTableSize = this._fitTableSize.bind(this);

        this._matchIconFilter = document.createElement("base-icon-gl");
        this._matchIconFilter.setDarkTheme(true);
        this._matchIconOrgVis = document.createElement("base-icon-gl");
        this._matchIconOrgVis.setDarkTheme(true);
        this._rootElem = this._createRootElement();

        this._errorContainer = document.createElement("div");
        this._errorContainer.classList.add("error");

        this._lastModifiedContainer = document.createElement("div");
        this._lastModifiedContainer.classList.add("last-modified-container");

        this._onAdvFilterRuleChange = this._onAdvFilterRuleChange.bind(this);
        this._clickHandler = this._clickHandler.bind(this);

        /* form controls */

        this._roleEnabledCheckBox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Enabled",
            title: "Allow the role to be assigned to users in this organization",
            disabled: false,
        });
        this._roleSharedCheckBox = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Shared",
            title: "Allow the role to be assigned to users in child organizations; however, the parent manages the role",
            disabled: false,
        });

        this._roleNameText = new InputTextControl({
            darkTheme: true,
            invalid: false,
            placeholder: "Name",
        });
        this._roleDescriptionText = new InputTextControl({
            darkTheme: true,
            invalid: false,
            placeholder: "Description",
        });

        this._compareWith = new ComboBoxControl({
            showFilter: false,
            adjustWidth: true,
            darkTheme: true,
        });

        /* form controls */
        /*
        Fields for filters

        - Custom Fiends (Bool, Dropdown and Text)
        - Vendor
        - Type
        - Name
        - Hostname
        */

        const VENDOR_OPTIONS = this._model._vendorMap;
        let optionVenderList = {};
        _.sortBy(Object.keys(VENDOR_OPTIONS)).map(function (key) {
            Object.assign(optionVenderList, {[VENDOR_OPTIONS[key]]: VENDOR_OPTIONS[key]});
        });

        const DEVICE_TYPES = this._model._typeBaseMap;
        let optionFilterList = {};
        _.sortBy(Object.keys(DEVICE_TYPES)).map(function (key) {
            Object.assign(optionFilterList, {[DEVICE_TYPES[key]]: DEVICE_TYPES[key]});
        });

        const optionMapSitePaths = this._model._sitePaths.map(({id, path}) => ({
            [id]: path,
        }));

        const DEVICE_ENVIRONMENTS = [
            {
                value: "PRODUCTION",
                title: "Production",
                label: "Production",
            },
            {
                value: "TEST",
                title: "Test",
                label: "Test",
            },
            {
                value: "PRODUCTION-TEST",
                title: "Production/Test",
                label: "Production/Test",
            },
        ];

        this._staticFilterOptions = [
            {
                id: "detectedVendor",
                field: "detectedVendor",
                name: "detectedVendor",
                label: "Vendor",
                dataType: "enum",
                options: optionVenderList,
            },
            {
                id: "detectedTypeBase",
                field: "detectedTypeBase",
                label: "Type",
                dataType: "enum",
                options: optionFilterList,
                name: "detectedTypeBase",
            },
            {
                id: "name",
                field: "name",
                label: "Name",
                name: "name",
                dataType: "string",
            },
            {
                id: "detectedHostName",
                field: "detectedHostName",
                label: "Hostname",
                dataType: "string",
                name: "detectedHostName",
            },
            {
                id: "environment",
                field: "environment",
                label: "Environment",
                options: DEVICE_ENVIRONMENTS,
                dataType: "enum",
                name: "environment",
            },
            {
                id: "siteName",
                field: "siteName",
                label: "Site Name",
                dataType: "string",
                name: "siteName",
            },
            {
                id: "siteCodeName",
                field: "siteCodeName",
                name: "siteCodeName",
                label: "Site Code Name",
                dataType: "string",
            },
            {
                id: "sitePath",
                field: "sitePath",
                name: "sitePath",
                label: "Site Path",
                dataType: "string",
            },
            {
                name: "siteId",
                field: "siteId",
                id: "siteId",
                label: "Site Path List",
                dataType: "pathlist",
                options: optionMapSitePaths,
            },
            {
                id: "siteLocationType",
                field: "siteLocationType",
                name: "siteLocationType",
                label: "Site Type",
                options: Object.entries(this._model._siteEntity.getLocationTypeLabels()).map(
                    ([value, title]) => ({name: value, title})
                ),
            },
        ];

        /* Slick Grid */
        this._tableOptions = {
            editable: !(this._model._isSeeded || this._model._isInherited) ? true : false,
            enableAddRow: false,
            enableCellNavigation: true,
            enableColumnReorder: false,
            forceFitColumns: true,
            enableAsyncPostRender: true,
            asyncEditorLoading: false,
            rowHeight: 30,
            headerRowHeight: 30,
            autoHeight: false,
            asyncPostRenderDelay: 0,
            autoEdit: false,
        };

        /* Slick Grid : Permissions - Start */

        this._permissionsContainer = document.createElement("permissions-container");
        this._permissionSTContainer = document.createElement("permissions-table");
        this._permissionsST = undefined;

        this._permissionsContainer.appendChild(this._permissionSTContainer);

        this._noCompareColumns = [
            {
                id: "Description",
                width: 430,
                field: "description",
                resizable: false,
                formatter: formatter.descriptionFormatter,
            },
            {
                id: "newrole",
                width: 270,
                name: "Role",
                toolTip: "Give the role this permission",
                field: "newrole",
                resizable: false,
                formatter: formatter.checkboxFormatter,
                editor: checkBoxEditor,
                cssClass: "centered newrole",
                headerCssClass: "centered",
            },
        ];

        this._columns = [
            {
                id: "Description",
                width: 430,
                field: "description",
                resizable: false,
                formatter: formatter.descriptionFormatter,
            },
            {
                id: "newrole",
                width: 90,
                name: "Role",
                toolTip: "Give the role this permission",
                field: "newrole",
                resizable: false,
                formatter: formatter.checkboxFormatter,
                editor: checkBoxEditor,
                cssClass: "centered newrole",
                headerCssClass: "centered",
            },
            {
                id: "compare",
                width: 90,
                name: "Compare",
                toolTip: "Gluware role that the role is compared to has the permission",
                field: "compare",
                resizable: false,
                formatter: formatter.checkboxFormatter,
                cssClass: "centered",
                headerCssClass: "centered",
            },
            {
                id: "match",
                width: 90,
                name: "Match",
                toolTip:
                    "The role and the role it's compared to both have or don't have the permission",
                field: "match",
                resizable: false,
                formatter: formatter.matchCheckboxFormatter,
                cssClass: "centered",
                headerCssClass: "centered",
                asyncPostRender: this._toggleCompareContainerPermissions.bind(this),
            },
        ];
        /* Slick Grid : Permissions - End */

        /* Slick Grid : Email Notifications - Start */
        this._emailContainer = document.createElement("email-container");
        this._emailSTContainer = document.createElement("email-table");

        this._emailST = undefined;

        this._emailContainer.appendChild(this._emailSTContainer);

        this._noCompareEmailColumns = [
            {
                id: "Description",
                width: 300,
                field: "description",
                resizable: false,
                formatter: formatter.descriptionFormatter,
            },
            {
                id: "emailAddresses",
                width: 400,
                name: "Email Addresses",
                toolTip: "Email Addresses",
                field: "emailAddresses",
                editor: InputTextEditor,
                formatter: formatter.emailFormatter,
                resizable: false,
                cssClass: "centered email",
                headerCssClass: "centered",
            },
        ];

        this._emailColumns = [
            {
                id: "Description",
                width: 210,
                field: "description",
                resizable: false,
                formatter: formatter.descriptionFormatter,
            },
            {
                id: "emailAddresses",
                width: 200,
                name: "Email Addresses",
                toolTip: "Email Addresses",
                field: "emailAddresses",
                editor: InputTextEditor,
                formatter: formatter.emailFormatter,
                resizable: false,
                cssClass: "centered email",
                headerCssClass: "centered",
            },
            {
                id: "compare",
                width: 200,
                name: "Compare",
                toolTip: "Compare with the email addresses from another role",
                field: "compare",
                resizable: false,
                cssClass: "centered compare-email",
                headerCssClass: "centered",
            },
            {
                id: "match",
                width: 90,
                name: "Match",
                toolTip: "The email addresses are the same as the role being compared with",
                field: "match",
                resizable: false,
                formatter: formatter.checkboxFormatter,
                cssClass: "centered",
                headerCssClass: "centered",
                asyncPostRender: this._toggleCompareContainerEmails.bind(this),
            },
        ];
        /* Slick Grid : Email Notifications - End */

        /* Slick Grid */

        this._cancelButton = new ButtonControl({
            label: "Cancel",
            title: "Cancel",
            darkTheme: true,
            disabled: false,
        });

        this._confirmButton = new ButtonControl({
            label: "Save",
            title: "Save",
            darkTheme: true,
            disabled: true,
            active: true,
        });

        /* Advanced filter : Start */
        this._filterMatchContainer = document.createElement("filter-match-container");
        this._filterMatchParent = document.createElement("div");
        this._filterMatchParent.classList.add("filter-match-parent");
        this.advancedFilterContainer = document.createElement("advanced-filter-container");

        this._createAdvancedFilter(this.advancedFilterContainer, this._filterMatchParent);

        /* Org Visibility Start */
        this._orgVisMatchContainer = document.createElement("orgvis-match-container");
        this._orgVisMatchParent = document.createElement("div");
        this._orgVisMatchParent.classList.add("orgvis-match-parent");
        this._orgVisContainer = document.createElement("orgvis-container");
        this._orgVisDisabledMsg = document.createElement("div");
        this._orgVisDisabledMsg.classList.add("orgvis-disable-msg");
        this._orgVisDisabledReadOnlyMsg = document.createElement("div");
        this._orgVisDisabledReadOnlyMsg.classList.add("orgvis-readonly-disable-msg");

        this._createOrgVisibility(this._orgVisContainer, this._orgVisMatchParent);

        this.render();
    }

    get rootElement() {
        return this.getRootElement();
    }

    get model() {
        return this.getModel();
    }

    /*******************************************/
    /* START PUBLIC COMPONENT RENDERER METHODS */
    /*******************************************/
    render() {
        if (this._renderProm === null) {
            return Promise.resolve();
        }
        this._renderProm = this._renderProm || this._render();
        return this._renderProm;
    }

    shutdown() {
        if (this._shutdownProm === null) {
            return Promise.resolve();
        }
        this._shutdownProm = this._shutdownProm || this._shutdown();
        return this._shutdownProm;
    }

    getRootElement() {
        return this._rootElem;
    }

    getModel() {
        return this._model;
    }

    /********************************************/
    /* END PUBLIC COMPONENT RENDERER METHODS */
    /* START PRIVATE COMPONENT RENDERER METHODS */

    /********************************************/

    _registerListeners() {
        this._model.on("Data", this._onData.bind(this));
        this._model.on("Error", this._onError.bind(this));

        document.addEventListener("click", this._clickHandler);
        window.addEventListener("resize", this._fitTableSize);

        this._model.on("DirtyStateChanged", this._onDirtyStateChanged.bind(this));

        this._compareWith.on("UserValue", this._onCompareWithChange.bind(this));

        /* Change Events Register - Start */
        this._roleEnabledCheckBox.on("UserValue", this._onEnabledCbxChange.bind(this));
        this._roleSharedCheckBox.on("UserValue", this._onSharedCbxChange.bind(this));
        this._roleNameText.on("UserValue", this._onNametbxChange.bind(this));
        this._roleDescriptionText.on("UserValue", this._onDesctbxChange.bind(this));
        this._confirmButton.on("UserClick", this._submitAction.bind(this));
        this._cancelButton.on("UserClick", this._onCancelClick.bind(this));
        /* Change Events Register - End */

        if (this._permissionsST) {
            this._permissionsST.onCellChange.subscribe(
                this._onPermissionCellChangeAction.bind(this)
            );
            // this._permissionsST.onBeforeEditCell.subscribe(this._onBeforeEditCell.bind(this));
        }
        if (this._emailST) {
            this._emailST.onCellChange.subscribe(this._onEmailCellChangeAction.bind(this));
        }

        /* org vis Events */
        this._onOrgVisEnabledChange.on("UserValue", this._onOrgVisEnabledChangeChange.bind(this));
        this._orgVisibility.on(
            "UserValue",
            (this._orgVisVisibilityChanger = this._orgVisVisibilityChanger.bind(this))
        );
        this._orgVisSomeSelections.on(
            "UserValue",
            (this._orgVisSomeSelectionChange = this._orgVisSomeSelectionChange.bind(this))
        );
    }

    _unregisterListeners() {
        this._model.removeListener("Data", this._onData);
        this._model.removeAllListeners("Error");

        document.removeEventListener("click", this._clickHandler);
        window.removeEventListener("resize", this._fitTableSize);
        this._compareWith.removeAllListeners("UserValue");

        /* Change Events UnRegister - Start */
        this._roleEnabledCheckBox.removeAllListeners("UserValue");
        this._roleSharedCheckBox.removeAllListeners("UserValue");
        this._roleNameText.removeAllListeners("UserValue");
        this._roleDescriptionText.removeAllListeners("UserValue");

        this._onOrgVisEnabledChange.removeAllListeners("UserValue");
        this._orgVisibility.removeAllListeners("UserValue");
        this._orgVisSomeSelections.removeAllListeners("UserValue");

        this._confirmButton.removeAllListeners("UserClick");
        this._cancelButton.removeAllListeners("UserClick");
        /* Change Events UnRegister - End */

        /* Slick Grid Events - Start */
        if (this._permissionsST) {
            this._permissionsST.onCellChange.unsubscribe(this._onPermissionCellChangeAction);
        }
        if (this._emailST) {
            this._emailST.onCellChange.unsubscribe(this._onEmailCellChangeAction);
        }
        /* Slick Grid Events - End */
        this._advancedFilter.removeListener(
            "AdvancedFilterRuleChanged",
            this._onAdvFilterRuleChange
        );
    }

    _registerAdvancedFilterListeners() {
        this._advancedFilter.on("AdvancedFilterRuleChanged", this._onAdvFilterRuleChange);
    }

    _render() {
        this.model.userFeedback.showLoadingProgress("Loading ...");
        const root = document.createDocumentFragment();

        if (_.isEmpty(this._model._copyFrom) || this._model._copyFrom === 0) {
            this._model._copyFrom = [];
        }

        return this._model
            .getRoles()
            .then(() => {
                return this.getSlickData(this._model._copyFrom);
            })
            .then((slickData) => {
                this._permissionsSTDataVIew = new Slick.Data.DataView();
                this._permissionsSTDataVIew.setItems(slickData.permissionsData);

                this._permissionsSTDataVIew.getItemMetadata = (
                    (original_getItemMetadata) => (row) => {
                        const item = this._permissionsSTDataVIew.getItem(row);
                        let ret = original_getItemMetadata(row);
                        if (item.title) {
                            ret = ret || {};
                            ret.cssClasses =
                                (ret.cssClasses || "") +
                                " " +
                                "title-row" +
                                " " +
                                item.category.replaceAll(" ", "").toLowerCase();
                        } else if (
                            !item.available ||
                            this._model._isInherited ||
                            this._model._isSeeded ||
                            !this._model._isEditable
                        ) {
                            ret = ret || {};
                            ret.cssClasses =
                                (ret.cssClasses || "") +
                                " " +
                                "readonly-row" +
                                " " +
                                item.category.replaceAll(" ", "").toLowerCase();
                        } else {
                            ret = ret || {};
                            ret.cssClasses =
                                (ret.cssClasses || "") +
                                " " +
                                item.category.replaceAll(" ", "").toLowerCase();
                        }
                        return ret;
                    }
                )(this._permissionsSTDataVIew.getItemMetadata);

                // this._setTitleStatus();

                this._emailSTDataVIew = new Slick.Data.DataView();
                this._emailSTDataVIew.setItems(slickData.emailData);

                this._emailSTDataVIew.getItemMetadata = ((original_getItemMetadata) => (row) => {
                    const item = this._emailSTDataVIew.getItem(row);
                    let ret = original_getItemMetadata(row);
                    if (item.title) {
                        ret = ret || {};
                        ret.cssClasses = (ret.cssClasses || "") + " " + "title-row";
                    } else if (this._model._isInherited || this._model._isSeeded) {
                        ret = ret || {};
                        ret.cssClasses = (ret.cssClasses || "") + " " + "readonly-row";
                    }
                    return ret;
                })(this._emailSTDataVIew.getItemMetadata);

                if (this._model._isSeeded || this._model._isInherited) {
                    if (!this._model._isInherited) {
                        Object.assign(this._tableOptions, {editable: true});
                    }
                }

                // Set height to slickGrid
                this.setSlickHeight();

                return Promise.resolve();
            })
            .then(() => {
                root.appendChild(this._createForm());

                this._rootElem.appendChild(root);

                if (this._model._actionGroupEntity.isDirty()) {
                    this.enableDisableSubmitButton(false);
                }

                return this._setPristineProperties();
            })
            .then(() => {
                if (this._model._compareActionGroup) {
                    this._permissionsST = new Slick.Grid(
                        this._permissionSTContainer,
                        this._permissionsSTDataVIew,
                        this._columns,
                        this._tableOptions
                    );

                    this._emailST = new Slick.Grid(
                        this._emailSTContainer,
                        this._emailSTDataVIew,
                        this._emailColumns,
                        this._tableOptions
                    );
                } else {
                    this._permissionsST = new Slick.Grid(
                        this._permissionSTContainer,
                        this._permissionsSTDataVIew,
                        this._noCompareColumns,
                        this._tableOptions
                    );

                    this._emailST = new Slick.Grid(
                        this._emailSTContainer,
                        this._emailSTDataVIew,
                        this._noCompareEmailColumns,
                        this._tableOptions
                    );
                }
                if (this._model._isSeeded || this._model._isInherited) {
                    this._permissionsST.getViewportNode().classList.add("seeded");
                    this._emailST.getViewportNode().classList.add("seeded");
                }

                this._fitTableSize();
                this._setTitleReadOnly();

                this._filterValue = this._model._actionGroupEntity.deviceFilter;
                if (this._model._isSeeded || this._model._isInherited) {
                    this._advancedFilter._renderer._rootElem.classList.add("seeded");
                    if (this._filterValue && (this._filterValue.$and || this._filterValue.$or)) {
                        this._advancedFilter._renderer.$builderContainer.queryBuilder(
                            "setRules",
                            this._getReadOnlyRule(this._filterValue)
                        );
                    } else {
                        this._advancedFilter._renderer.$builderContainer.queryBuilder(
                            "setRules",
                            this._getReadOnlyRule({$and: []})
                        );
                        this._advancedFilter._renderer.$builderContainer.html(
                            this._createFilterEmptyElement(this._model._actionGroupEntity.name)
                        );
                        this._advancedFilter._renderer._rootElem.classList.add("empty-filter");
                    }
                } else {
                    if (this._filterValue && (this._filterValue.$and || this._filterValue.$or)) {
                        this._advancedFilter._renderer.$builderContainer.queryBuilder(
                            "setRulesFromMongo",
                            this._filterValue
                        );
                    }
                }

                this._buildEditableManagedOrgList();

                this._compareFilterValues();
                this._compareOrgSelectionValues();
                this._updateLastModified();
                this._registerListeners();
                // this.setSlickGridTableData(slickData);
                this._registerAdvancedFilterListeners();

                return Promise.resolve();
            });
    }

    setSlickHeight() {
        let heightVal = 0;
        if (this._permissionsSTDataVIew && this._permissionsSTDataVIew.getItems()) {
            heightVal = 30 * this._permissionsSTDataVIew.getItems().length + 24;
            this._permissionSTContainer.setAttribute("style", `height: ${heightVal}px;`);
        }
        if (this._emailSTDataVIew && this._emailSTDataVIew.getItems()) {
            heightVal = 30 * this._emailSTDataVIew.getItems().length + 24;
            this._emailSTContainer.setAttribute("style", `height: ${heightVal}px;`);
        }
    }

    getSlickData(compareId) {
        return Promise.resolve()
            .then(() => {
                if (!_.isEmpty(compareId) && compareId !== this._model._rolesArray[0].value) {
                    return this._model._retrieveActionGroup(compareId);
                }
            })
            .then((actionGroupEntityToCompare) => {
                this._model._compareActionGroup = actionGroupEntityToCompare;
                if (compareId && actionGroupEntityToCompare) {
                    this._readOnlyFilterValue = this._model._compareActionGroup.getDeviceFilter(); //set new value from entity
                    this._readOnlyOrgSelections = this._model._compareActionGroup.getVisibleOrgs();
                    this._orgVisibilityReadOnly.value = this._model._compareActionGroup.getOrgVis();
                }

                //filter form set and compare
                if (actionGroupEntityToCompare) {
                    this._advancedFilterReadonly._renderer._rootElem.style.display = "block";
                    this._filterMatchParent.style.display = "block";
                    this._advancedFilter._renderer._rootElem.style.width = "90%";

                    this.advancedFilterContainer.classList.remove("no-compare");
                } else {
                    this._advancedFilterReadonly._renderer._rootElem.style.display = "none";
                    this._filterMatchParent.style.display = "none";
                    this._advancedFilter._renderer._rootElem.style.width = "75%";

                    this.advancedFilterContainer.classList.add("no-compare");
                }
                let readOnlyFilterValue;
                if (
                    !this._readOnlyFilterValue ||
                    !(this._readOnlyFilterValue.$and || this._readOnlyFilterValue.$or)
                ) {
                    readOnlyFilterValue = {
                        $and: [],
                    };
                } else {
                    readOnlyFilterValue = this._readOnlyFilterValue;
                }
                this._advancedFilterReadonly._renderer.$builderContainer.html("");
                this._advancedFilterReadonly._renderer._rootElem.classList.remove("empty-filter");
                this._advancedFilterReadonly._renderer.$builderContainer.queryBuilder(
                    "setRules",
                    this._getReadOnlyRule(readOnlyFilterValue)
                );

                if (
                    !this._readOnlyFilterValue ||
                    !(this._readOnlyFilterValue.$and || this._readOnlyFilterValue.$or)
                ) {
                    this._advancedFilterReadonly._renderer.$builderContainer.html(
                        this._createFilterEmptyElement(
                            (this._model._compareActionGroup &&
                                this._model._compareActionGroup.name) ||
                                ""
                        )
                    );
                    this._advancedFilterReadonly._renderer._rootElem.classList.add("empty-filter");
                }

                //orgVis readonly form set and compare
                this._orgVisSomeSelectionsReadOnly.value = this._orgConvertToSelection(
                    this._readOnlyOrgSelections
                );
                this._compareOrgSelectionValues();

                if (actionGroupEntityToCompare) {
                    this._orgVisGroupedControlsReadOnly.style.display = "block";
                    this._orgVisMatchParent.style.display = "block";
                    this._orgVisGroupedControls.classList.add("compared");
                    this._orgVisGroupedControlsReadOnly.classList.add("compared");

                    this._orgVisSomeSelectionsReadOnly.renderer._rootElem.classList.add("compare");
                    this._orgVisSomeSelections.renderer._rootElem.classList.add("compare");
                } else {
                    this._orgVisGroupedControlsReadOnly.style.display = "none";
                    this._orgVisMatchParent.style.display = "none";
                    this._orgVisGroupedControls.classList.remove("compared");
                    this._orgVisGroupedControlsReadOnly.classList.remove("compared");

                    this._orgVisSomeSelectionsReadOnly.renderer._rootElem.classList.remove(
                        "compare"
                    );
                    this._orgVisSomeSelections.renderer._rootElem.classList.remove("compare");
                }

                return Promise.resolve();
            })
            .then(() => {
                let actionGroupEntity;
                if (_.isEmpty(this._model._actionGroupToCopyFrom)) {
                    actionGroupEntity = this._model._actionGroupEntity;
                } else {
                    actionGroupEntity = this._model._actionGroupToCopyFrom;
                }
                return this._model.convertPermissionsDataToSlickFormat(actionGroupEntity);
            });
    }

    _shutdown() {
        this._unregisterListeners();
        this._rootElem.remove();
        this._rootElem = null;

        if (this._permissionsST && this._permissionsST.destroy) {
            this._permissionsST.destroy();
        }
        if (this._emailST && this._emailST.destroy) {
            this._emailST.destroy();
        }

        return Promise.all([])
            .then(() => {
                this._model = null;
                this._shutdownProm = null;
                this._permissionsST = undefined;
                this._emailST = undefined;
                this._renderProm = null;
                this._roleNameText = undefined;
                this._roleDescriptionText = undefined;
                this._roleEnabledCheckBox = undefined;
                this._roleSharedCheckBox = undefined;
                this._compareWith = undefined;
                this._staticFilterOptions = undefined;
                this._advancedFilter = undefined;
                this._advancedFilterReadonly = undefined;
                this.advancedFilterContainer = undefined;
                this._readOnlyFilterValue = null;
                this._readOnlyOrgSelections = null;
                this._filterValue = null;
                this._filterMatchContainer = undefined;
                this._filterMatchParent = undefined;
                this._orgVisMatchContainer = undefined;
                this._orgVisMatchParent = undefined;
                this._orgVisContainer = undefined;
                this._orgVisDisabledMsg = undefined;
                this._orgVisDisabledReadOnlyMsg = undefined;
                this._orgVisMatchContainer = undefined;
                this._orgVisMatchParent = undefined;

                this._fitTableSize = undefined;
                this._matchIconFilter.remove();
                this._matchIconFilter = undefined;
                this._matchIconOrgVis.remove();
                this._matchIconOrgVis = undefined;
                this._errorContainer.remove();
                this._errorContainer = undefined;
                this._lastModifiedContainer.remove();
                this._lastModifiedContainer = undefined;
                this._onAdvFilterRuleChange = undefined;
                this._clickHandler = undefined;
                this._tableOptions = undefined;

                this._permissionSTContainer.remove();
                this._permissionSTContainer = undefined;
                this._columns = undefined;
                this._permissionsContainer.remove();
                this._permissionsContainer = undefined;
                this._emailContainer.remove();
                this._emailContainer = undefined;
                this._emailSTContainer.remove();
                this._emailSTContainer = undefined;
                this._emailColumns = undefined;
                this._cancelButton = undefined;
                this._confirmButton = undefined;
            })
            .catch((error) => {
                console.log(`${this.constructor.name}: error in shutdown`);
                console.error(error);
            });
    }

    /*******************************************/
    /* END PRIVATE COMPONENT RENDERER METHODS  */
    /* START PRIVATE RENDERER METHODS */

    /*******************************************/

    _updateLastModified() {
        const lastModifiedBy = this._model._actionGroupEntity.updatedBy;
        let lastModifiedOn = this._model._actionGroupEntity.updatedOn;
        if (lastModifiedBy && lastModifiedOn) {
            this._lastModifiedContainer.innerHTML = `
            <span>Last modified by ${lastModifiedBy} 
            on ${this._setDate(lastModifiedOn)}
            </span>
            `;
        } else {
            this._lastModifiedContainer.innerHTML = ``;
        }
    }

    _createRootElement() {
        const rootElement = document.createElement("div");
        rootElement.classList.add("add-edit-user-role-parent");
        rootElement.component = this._model;
        return rootElement;
    }

    _createForm() {
        const formRootFragment = document.createDocumentFragment();

        const formRoot = document.createElement("div");
        formRoot.classList.add("add-edit-user-role", "modal_feedback_container");

        const title = document.createElement("h2");
        title.innerText = MODAL_TITLE_ADD;

        const formContainer = document.createElement("action-groups-form-container");
        const formActionContainer = document.createElement("actions-container");
        const roleNameLabel = document.createElement("label");
        roleNameLabel.innerText = "Name";

        const roleDescriptionLabel = document.createElement("label");
        roleDescriptionLabel.innerText = "Description";
        roleDescriptionLabel.title = "Describe the role";

        const compareWithLabel = document.createElement("label");
        compareWithLabel.innerText = "Compare with";
        compareWithLabel.title = "Display permissions of another role for comparison";

        const permissionsTitle = document.createElement("label");
        permissionsTitle.innerText = "Permissions";

        const emailTitle = document.createElement("label");
        emailTitle.classList.add("spacer-3");
        emailTitle.innerText = "Email Notification Recipients";

        const shareEnableContainer = document.createElement("div");
        shareEnableContainer.classList.add("share-enable-container");

        const compareWithContainer = document.createElement("div");
        compareWithContainer.classList.add("compare-with-container");
        const compareWithInner = document.createElement("div");
        compareWithInner.classList.add("compare-with-inner");

        const filterSectionContainer = document.createElement("filter-section");
        const filterSectionLabel = document.createElement("label");
        filterSectionLabel.classList.add("spacer-3");

        filterSectionLabel.innerText = "Device Filter";
        filterSectionContainer.appendChild(filterSectionLabel);
        filterSectionContainer.appendChild(this.advancedFilterContainer);
        this._filterMatchParent.appendChild(this._filterMatchContainer);
        // this._createAdvancedFilter(advancedFilterContainer, this._filterMatchParent);
        this._compareFilterValues();

        //org visibility section
        const orgVisSectionContainer = document.createElement("orgvis-section");
        const orgVisSectionLabel = document.createElement("label");
        orgVisSectionLabel.classList.add("spacer-3");

        orgVisSectionLabel.innerText = "Organization Visibility";
        orgVisSectionContainer.appendChild(orgVisSectionLabel);

        orgVisSectionContainer.appendChild(this._orgVisContainer);
        this._orgVisMatchParent.appendChild(this._orgVisMatchContainer);
        this._compareOrgSelectionValues();

        this._controlButtonsContainer = document.createElement("div");
        this._controlButtonsContainer.classList.add("control-buttons");
        this._controlButtonsContainer.appendChild(this._cancelButton.renderer.getRootElement());
        this._controlButtonsContainer.appendChild(this._confirmButton.renderer.getRootElement());

        formContainer.appendChild(title);

        formContainer.appendChild(roleNameLabel);
        formContainer.appendChild(this._roleNameText.renderer.getRootElement());

        formContainer.appendChild(roleDescriptionLabel);
        formContainer.appendChild(this._roleDescriptionText.renderer.getRootElement());

        shareEnableContainer.appendChild(this._roleEnabledCheckBox.renderer.getRootElement());
        shareEnableContainer.appendChild(this._roleSharedCheckBox.renderer.getRootElement());
        formContainer.appendChild(shareEnableContainer);

        compareWithInner.appendChild(compareWithLabel);
        compareWithInner.appendChild(this._compareWith.renderer.getRootElement());
        compareWithContainer.appendChild(document.createElement("hr"));
        compareWithContainer.appendChild(compareWithInner);
        formContainer.appendChild(compareWithContainer);

        formContainer.appendChild(permissionsTitle);
        formContainer.appendChild(this._permissionsContainer);

        formContainer.appendChild(filterSectionContainer);

        //org container goes here
        formContainer.appendChild(orgVisSectionContainer);

        formContainer.appendChild(emailTitle);
        formContainer.appendChild(this._emailContainer);
        formActionContainer.appendChild(this._lastModifiedContainer);
        formActionContainer.appendChild(this._controlButtonsContainer);

        formRoot.appendChild(formContainer);

        formRootFragment.appendChild(formRoot);
        formRootFragment.appendChild(formActionContainer);

        return formRootFragment;
    }

    _createAdvancedFilter(filterContainer, filterMatchParent) {
        const customFieldsList = [];

        for (let field in this._model._customFieldsWithInherited) {
            if (
                this._model._customFieldsWithInherited.hasOwnProperty(field) &&
                !this._model._customFieldsWithInherited[field].systemOwned &&
                ["Boolean", "String", "Enum", "Dropdown"].indexOf(
                    this._model._customFieldsWithInherited[field].dataType
                ) >= 0
            ) {
                customFieldsList.push(
                    Object.assign({}, this._model._customFieldsWithInherited[field], {
                        id: field,
                        name: `customFieldValues.${field}`,
                        label: this._model._customFieldsWithInherited[field].name,
                    })
                );
            }
        }

        this._advancedFilter = new AdvancedFilter({
            filterData: {},
            columnsConfig: this._getFilterOptions(this._staticFilterOptions, customFieldsList),
            allowGroups: 1,
        });
        filterContainer.appendChild(this._advancedFilter.getRootElement());

        //expected from entity: {"$and":[{"Name":{"$regex":"^aaa","$options":"i"}},{"selectType":{"$eq":"d790661a-9183-4f67-88cb-2a343ae66f23"}},{"CheckBox":true}]}

        this._advancedFilterReadonly = new AdvancedFilter({
            filterData: {},
            columnsConfig: this._getFilterOptions(this._staticFilterOptions, customFieldsList),
            allowGroups: 1,
        });

        filterContainer.appendChild(this._advancedFilterReadonly.getRootElement());
        filterContainer.appendChild(filterMatchParent);

        // advFil._renderer.$builderContainer.queryBuilder('getMongo') - returns selected query to be called in save
        // advFil._renderer.$builderContainer.queryBuilder('getRulesFromMongo', temp1) - returns rules from mongo query
        // advFil._renderer.$builderContainer.queryBuilder('setRulesFromMongo', temp1)
        // advFil._renderer.$builderContainer.queryBuilder('setRules', rule)
    }

    _getFilterOptions(columnsData, customFields) {
        const filterOptions = [].concat(columnsData, customFields);
        const allowedCustomFieldTypes = ["boolean", "string", "enum", "pathlist"];
        return filterOptions
            .filter((option) => {
                if (
                    option.dataType &&
                    !allowedCustomFieldTypes.includes(option.dataType.toLowerCase())
                ) {
                    return false;
                }
                return true;
            })
            .map((optData) => {
                const options = {
                    dataType: optData.dataType ? optData.dataType.toLowerCase() : undefined,
                };
                return Object.assign({}, optData, options);
            });
    }

    _getReadOnlyRule(mongoQuery) {
        const builderContainer = this._advancedFilter._renderer.$builderContainer;
        const transformedQuery = builderContainer.queryBuilder("getRulesFromMongo", mongoQuery);
        transformedQuery.rules = transformedQuery.rules.map((rule) => {
            rule.readonly = true;
            return rule;
        });
        transformedQuery.readonly = true;
        return transformedQuery;
    }

    _setDisabled() {
        this._roleEnabledCheckBox.setDisabled(true);
        this._roleSharedCheckBox.setDisabled(true);
        this._roleNameText.setDisabled(true);
        this._roleDescriptionText.setDisabled(true);
        this._advancedFilter._renderer.resetBtn.setDisabled(true);
    }

    //org visibility form funcs
    _createOrgVisibility(orgVisContainer, orgVisMatchParent) {
        this._onOrgVisEnabledChange = new CheckboxControl({
            darkTheme: true,
            labelShown: true,
            label: "Allow Role-based Organization Visibility",
            title: "Allow Role-based Organization Visibility",
            disabled: false,
        });
        this._orgVisibility = new InputRadioControl({darkTheme: true, horizontal: true});
        this._orgVisSomeSelections = new MultiCombobox({darkTheme: true});

        this._orgVisGroupedControls = document.createElement("div");
        this._orgVisGroupedControlsReadOnly = document.createElement("div");
        this._orgVisGroupedControls.classList.add("orgvis-grouped-controls");
        this._orgVisGroupedControlsReadOnly.classList.add("orgvis-grouped-controls-read-only");

        this._orgVisDisabledMsg.innerHTML =
            "Role-defined organization visibility is not available for shared roles.";

        this._orgVisGroupedControls.appendChild(
            this._onOrgVisEnabledChange.renderer.getRootElement()
        );
        this._orgVisGroupedControls.appendChild(this._orgVisibility.renderer.getRootElement());
        this._orgVisGroupedControls.appendChild(
            this._orgVisSomeSelections.renderer.getRootElement()
        );
        this._orgVisGroupedControls.appendChild(this._orgVisDisabledMsg);
        orgVisContainer.appendChild(this._orgVisGroupedControls);

        this._orgVisibilityReadOnly = new InputRadioControl({darkTheme: true, horizontal: true});
        this._orgVisibilityReadOnly.setDisabled(true);

        this._orgVisSomeSelectionsReadOnly = new MultiCombobox({darkTheme: true, disabled: true});
        this._orgVisGroupedControlsReadOnly.appendChild(
            this._orgVisibilityReadOnly.renderer.getRootElement()
        );
        this._orgVisGroupedControlsReadOnly.appendChild(
            this._orgVisSomeSelectionsReadOnly.renderer.getRootElement()
        );
        this._orgVisGroupedControlsReadOnly.appendChild(this._orgVisDisabledReadOnlyMsg);
        orgVisContainer.appendChild(this._orgVisGroupedControlsReadOnly);

        orgVisContainer.appendChild(orgVisMatchParent);
    }

    _buildEditableManagedOrgList() {
        let orgApiList = {};
        let orgOptions = [];

        this._orgVisSomeSelections.options = [];
        this._orgAllSelectedList = [];

        let creatorAssignableOrgs = this.model._descendantOrgs || [];

        function genericMultiSelectOption(orgName, orgId, isDisabled) {
            return {
                label: orgName,
                value: orgId,
                disabled: isDisabled,
            };
        }

        //For each authorized org, check if entry matches in current orgApi list
        //   If (yes) -  unlock the selection to be removable.
        //   If (no) - include the selection which can be added.
        Object.keys(creatorAssignableOrgs).forEach((orgId) => {
            if (orgId === this.model.orgId) {
                // NOTE: You should not be able to remove the selected role's home organization!
                return;
            }

            let orgName = creatorAssignableOrgs[orgId].name;
            if (orgApiList[orgId]) {
                //This option is in the edited role list, and the editor has the option to remove it.
                orgApiList[orgId].disabled = false;
            } else {
                //This option is in the editors list, and is available to be added to the role's list.
                orgApiList[orgId] = genericMultiSelectOption(orgName, orgId, false);
                //update the all list
                this._orgAllSelectedList.push(orgId);
            }
        });

        //Build the options array for the multi-select list.
        Object.keys(orgApiList).forEach((orgId) => {
            orgOptions.push(orgApiList[orgId]);
        });

        //Build the selected value list
        let selectedValueList = this._orgConvertToSelection(this.model.getVisibleOrgsList());

        this._orgVisSomeSelectionsReadOnly.options = orgOptions;
        this._orgVisSomeSelectionsReadOnly.value = this._orgConvertToSelection(
            this._readOnlyOrgSelections
        );
        this._orgVisSomeSelections.options = orgOptions;
        this._orgVisSomeSelections.value = selectedValueList;

        this._orgVisInit();
    }

    _orgVisInit() {
        const orgVisEnabledOnLoad = this.model.getOrgVis() !== "DISABLED";

        this._roleSharedCheckBox.setDisabled(orgVisEnabledOnLoad);
        this._onOrgVisEnabledChange.setDisabled(this._roleSharedCheckBox.value);
        this._setOrgVisMsg(this._roleSharedCheckBox.value);

        this._onOrgVisEnabledChange.value = orgVisEnabledOnLoad;
        this._orgVisibility.setDisabled(!orgVisEnabledOnLoad);
        this._orgVisSomeSelections.setDisabled(!orgVisEnabledOnLoad);
    }

    _onOrgVisEnabledChangeChange(newVal) {
        let msg = "";
        const newVisibilityValue = newVal === false ? "DISABLED" : "SOME";
        const defaultSelection = [];
        //enable/disable share
        if (newVal) {
            msg =
                "Enabling role-based organization visibility will replace the organization visibility settings for all user profiles linked to this role. Do you want to proceed?";
        } else {
            msg =
                "Disabling role-based organization visibility will reset organization visibility for all user profiles linked to this role. Their organization access will only include their home organization. Do you want to proceed?";
        }

        this._model._uiUtils
            .showDeleteConfirmDialog({
                //showConfirmDialog
                title: "Warning",
                confirmButtonName: "Confirm",
                cancelButtonName: "Cancel",
                message: msg,
            })
            .then(() => {
                //mutually exclusive form elements
                this._roleSharedCheckBox.setDisabled(newVal);
                if (newVal) {
                    this._roleSharedCheckBox.value = false;
                }

                this.model.setOrgVis(newVisibilityValue);
                this._orgVisibility.setDisabled(!newVal);
                this._orgVisSomeSelections.setDisabled(!newVal);
                this._orgVisibility.value = newVisibilityValue;
                this._orgVisVisibilityChanger(newVisibilityValue);
                this._orgVisSomeSelections.value = defaultSelection;
                this._orgVisSomeSelectionChange(defaultSelection);
            })
            .catch((error) => {
                if (error && !error.userRejected) {
                    console.log("Error in Delete: _deleteActionGroup()", error);
                    return this._model && this.model.userFeedback.displayError(error);
                } else {
                    this._onOrgVisEnabledChange.setValue(!this._onOrgVisEnabledChange.getValue());
                }
            });
    }
    _orgVisVisibilityChanger(newVal) {
        this.model.setOrgVis(newVal);
        if (newVal === "ALL") {
            //set all descendant orgs
            this._orgVisSomeSelections.value = this._orgAllSelectedList;
            this._orgVisSomeSelectionChange(this._orgAllSelectedList);
            this._orgVisSomeSelections.setDisabled(true);
        } else if (newVal === "SOME") {
            this._buildEditableManagedOrgList();
            let selectedOptionslist = this._orgVisSomeSelections.value;
            let possibleValues = {};
            selectedOptionslist.forEach((option) => {
                possibleValues[option] = true;
            });
            this._orgVisSomeSelections.setDisabled(false);
        }
        this._compareOrgSelectionValues();
    }

    _orgVisSomeSelectionChange(newVal) {
        //if newVal is missing this org's id and the control is active, prepend it
        if (
            newVal.find(this.model._activeOrgId) === undefined &&
            !this._orgVisSomeSelections.isDisabled()
        ) {
            newVal.unshift(this.model._activeOrgId);
        }
        this.model.setVisibleOrgsList(this._orgSelectionConversion(newVal));
        this._orgVisSomeSelections.value = newVal;
        this._compareOrgSelectionValues();
    }

    _orgConvertToSelection(modelOrgList) {
        let selectedValueList = [];
        //Build the selected value list
        Object.keys(modelOrgList || {}).forEach((orgId) => {
            selectedValueList.push(orgId);
        });

        return selectedValueList;
    }

    _orgSelectionConversion(val) {
        let selections = {};
        val.forEach((obj) => {
            selections[obj] = true;
        });
        return selections;
    }

    _compareOrgSelectionValues() {
        const selectionValuesMatch = this._doOrgVisListsMatch();
        this._setOrgVisMatchContainerHeight();
        if (selectionValuesMatch) {
            this._orgVisMatchContainer.innerHTML = "";
            this._matchIconOrgVis.dataset.icon = "application.check_sign";
            this._orgVisMatchContainer.appendChild(this._matchIconOrgVis);
        } else {
            this._orgVisMatchContainer.innerHTML = "";
            this._matchIconOrgVis.dataset.icon = "application.cross_mark";
            this._orgVisMatchContainer.appendChild(this._matchIconOrgVis);
        }
        if (this._isOrgVisReadOnlyDisabled()) {
            //show _orgVisReadOnlyMsg
            this._orgVisDisabledReadOnlyMsg.classList.add("show");
            this._orgVisDisabledReadOnlyMsg.innerHTML = `No Organization Visibility Defined for ${this._model._compareActionGroup.getName()} `;
            //hide the rest of readonly
            this._orgVisibilityReadOnly.renderer.getRootElement().classList.add("hide");
            this._orgVisSomeSelectionsReadOnly.renderer.getRootElement().classList.add("hide");
            this._orgVisibilityReadOnly.renderer.getRootElement().classList.add("hide");
        } else {
            this._orgVisDisabledReadOnlyMsg.classList.remove("show");
            this._orgVisSomeSelectionsReadOnly.renderer.getRootElement().classList.remove("hide");
            this._orgVisibilityReadOnly.renderer.getRootElement().classList.remove("hide");
        }
    }

    _isOrgVisReadOnlyDisabled() {
        if (this._model._compareActionGroup === undefined) {
            return false;
        }
        const compareOrgVis = this._model._compareActionGroup.getOrgVis() || "DISABLED";
        return compareOrgVis === "DISABLED";
    }

    _doOrgVisListsMatch() {
        if (this._model._compareActionGroup === undefined) {
            return false;
        }

        //special case where there is incorrect data:  undefined should === DISABLED or empty object
        const thisOrgVis = this.model.getOrgVis() || "DISABLED";
        const thisVisOrgList = this.model.getVisibleOrgsList() || {};
        const comparedOrgVis = this._model._compareActionGroup.getOrgVis() || "DISABLED";
        const compareVisOrgList = this._readOnlyOrgSelections || {};

        return thisOrgVis === comparedOrgVis && _.isEqual(thisVisOrgList, compareVisOrgList);
    }

    _setOrgVisMatchContainerHeight() {
        const orgVisHeight = this._orgVisGroupedControls.clientHeight;
        const readOnlyOrgVisHeight = this._orgVisGroupedControlsReadOnly.clientHeight;
        this._orgVisMatchParent.style.top = `${orgVisHeight / 2}px`;
        this._orgVisMatchParent.style.bottom = `${readOnlyOrgVisHeight / 2}px`;
        this._orgVisMatchContainer.style.top = `${orgVisHeight / 2 + 10}px`;
    }

    _setOrgVisMsg(value) {
        if (value) {
            this._orgVisDisabledMsg.classList.add("show");
            this._orgVisibility.renderer._rootElem.classList.add("hide");
            this._onOrgVisEnabledChange.renderer._rootElem.classList.add("hide");
        } else {
            this._orgVisDisabledMsg.classList.remove("show");
            this._orgVisibility.renderer._rootElem.classList.remove("hide");
            this._onOrgVisEnabledChange.renderer._rootElem.classList.remove("hide");
        }
        this._orgVisSomeSelections.setVisible(!value);
    }

    _setPristineProperties() {
        let actionGroupEntity;
        let copied = false;
        if (_.isEmpty(this._model._actionGroupToCopyFrom)) {
            actionGroupEntity = this._model._actionGroupEntity;
        } else {
            actionGroupEntity = this._model._actionGroupToCopyFrom;
            copied = true;
        }

        this._roleEnabledCheckBox.setValue(
            _.get(actionGroupEntity, "pristineProperties.enabled", false)
        );
        this._roleSharedCheckBox.setValue(
            copied ? false : _.get(actionGroupEntity, "pristineProperties.shared", false)
        );

        this._roleNameText.setValue(
            _.get(
                this._model._actionGroupEntity,
                "pristineProperties.name",
                !_.isEmpty(this._model._actionGroupEntity.getName())
                    ? this._model._actionGroupEntity.getName()
                    : ""
            )
        );
        this._roleDescriptionText.setValue(
            _.get(actionGroupEntity, "pristineProperties.description", "")
        );

        if (this._model._isSeeded || this._model._isInherited) {
            this._setDisabled();
        }

        //set org vis
        this._orgVisibility.setOptions(this._buildOrgVisibilitylList("ALL"));
        this._orgVisibility.value = this.model.getOrgVis();
        this._orgVisibilityReadOnly.setOptions(this._buildOrgVisibilitylList("ALL"));
        this._orgVisibilityReadOnly.setDisabled(true);
        this._orgVisSomeSelections.options = [];
        this._orgVisSomeSelections.value = [];

        this._compareWith.options = this._model._rolesArray;
        if (!_.isEmpty(this._model._copyFrom)) {
            this._compareWith.setSelectedOption(this._model._copyFrom);
        } else {
            this._compareWith.setSelectedOption(this._compareWith.getOptions()[0].value);
        }

        return Promise.resolve();
    }

    /*******************************************/
    /* END PRIVATE RENDERER METHODS  */
    /* START HANDLER METHODS */
    /*******************************************/

    // Compare function for Permissions
    _toggleCompareContainerPermissions(cellNode, row, currentRow) {
        if (_.get(currentRow, "title", false)) {
            return;
        }
        if (!_.isUndefined(currentRow.newrole)) {
            const checkbox = document.createElement("base-icon-gl");
            checkbox.setDarkTheme(true);
            if (_.get(currentRow, "newrole", false) !== _.get(currentRow, "compare", false)) {
                checkbox.dataset.icon = "application.cross_mark";
            } else {
                checkbox.dataset.icon = "application.check_sign";
            }
            if (cellNode[0].childElementCount > 1) {
                cellNode[0].childNodes[1].remove();
            }
            cellNode[0].appendChild(checkbox);
        }
    }

    // Compare function for Notification Emails
    _toggleCompareContainerEmails(cellNode, row, currentRow) {
        if (!_.isUndefined(currentRow.emailAddresses)) {
            // Split emails based on comma or space
            const currentEmails = _.filter(
                _.split(currentRow.emailAddresses, /[,\s]/),
                (emails) => !_.isEmpty(emails)
            );
            const compareEmails = _.filter(
                _.split(currentRow.compare, /[,\s]/),
                (emails) => !_.isEmpty(emails)
            );

            // Create the checkbox to show
            const checkbox = document.createElement("base-icon-gl");
            checkbox.setDarkTheme(true);

            // compare the sorted array
            if (!_.isEqual(currentEmails.sort(), compareEmails.sort())) {
                checkbox.dataset.icon = "application.cross_mark";
            } else {
                checkbox.dataset.icon = "application.check_sign";
            }
            if (cellNode[0].childElementCount > 1) {
                cellNode[0].childNodes[1].remove();
            }
            cellNode[0].appendChild(checkbox);
        }
    }

    _onData(data = {}) {
        this._setData(data);
    }

    _onError(errorMsg) {
        this._setError(errorMsg);
        this._checkSaveButton(false);
    }

    // Dirty State Change Event
    _onDirtyStateChanged(state) {
        this.enableDisableSubmitButton(!state);
    }

    // Compare with Dropdown Change Event
    _onCompareWithChange(actionGroupId) {
        this.getSlickData(actionGroupId).then((slickData) => {
            this.setSlickGridTableData(slickData);
            this._compareFilterValues();
            this._compareOrgSelectionValues();
        });
    }

    _onEnabledCbxChange(value) {
        this._model._actionGroupEntity.setProperty("enabled", value);
    }
    _onSharedCbxChange(value) {
        //mutually exclusive form elements
        this._onOrgVisEnabledChange.setDisabled(value);
        this._setOrgVisMsg(value);

        this._model._actionGroupEntity.setShared(value);
    }
    _onNametbxChange(value) {
        this._model._actionGroupEntity.setName(value);
    }
    _onDesctbxChange(value) {
        this._model._actionGroupEntity.setDescription(value);
    }

    _onPermissionCellChangeAction(e, {item, /*column , row, cell,*/ grid}) {
        const cat = item.category.replace(/ /g, "");
        const authItems = grid.getData().getItems();
        if (item.newrole && typeof item.newrole === "boolean") {
            this._model._actionGroupEntity.addActionAuth(item.name);
            this._model._catCounts[cat].count += 1;
        } else if (typeof item.newrole === "boolean") {
            this._model._actionGroupEntity.removeActionAuth(item.name);
            this._model._catCounts[cat].count -= 1;
        }

        if (typeof item.newrole === "string") {
            if (item.newrole === "all-selected") {
                this._model._catCounts[cat].count = this._model._catCounts[cat].total;
                this._setCatActionAuths(item.category, authItems, true);
            }
            if (item.newrole === "none-selected") {
                this._model._catCounts[cat].count = 0;
                this._setCatActionAuths(item.category, authItems, false);
            }
        }
        this._model._setCatStatus(authItems, this._model._catCounts);
        this._resetGrid(grid);
    }

    _setCatActionAuths(category, items, set = true) {
        if (set) {
            const catitems = items.filter((item) => {
                if (
                    item.category === category &&
                    !item.title &&
                    item.available &&
                    !this._model._isInherited &&
                    !this._model._isSeeded &&
                    this._model._isEditable
                ) {
                    item.newrole = true;
                    return true;
                }
            });
            catitems.forEach((item) => {
                this._model._actionGroupEntity.addActionAuth(item.name);
            });
        } else {
            const catitems = items.filter((item) => {
                if (
                    item.category === category &&
                    !item.title &&
                    item.available &&
                    !this._model._isInherited &&
                    !this._model._isSeeded &&
                    this._model._isEditable
                ) {
                    item.newrole = false;
                    return true;
                }
            });
            catitems.forEach((item) => {
                this._model._actionGroupEntity.removeActionAuth(item.name);
            });
        }
    }

    _resetGrid(grid) {
        grid.invalidateAllRows();
        grid.render();
    }

    _onEmailCellChangeAction(e, {item /*, column , row, cell, grid*/}) {
        this._model._actionGroupEntity.setActionDefaultEmailAddress(item.name, item.emailAddresses);
    }

    _onAdvFilterRuleChange(event) {
        let filterVal;
        try {
            filterVal = event.builder.getRules({skip_empty: true, allow_invalid: true});
        } catch (error) {
            if (typeof error.message !== "string") {
                error.message = "Not of type String!!";
            }
            const errorMessagesList = [
                "Cannot read property 'each' of null".toLowerCase().replace(/['" ]/gi, ""),
                "Cannot read property 'root' of null".toLowerCase().replace(/['" ]/gi, ""),
            ];
            if (errorMessagesList.indexOf(error.message.toLowerCase().replace(/['" ]/gi, "")) < 0) {
                this._model._data.manageRoleComponent._logger.warn(
                    {error},
                    "Advanced filter delete error"
                );
            }
        }

        if (_.get(filterVal, "rules", []).length > 0) {
            if (filterVal.valid) {
                this._filterValue = event.target.queryBuilder.getMongo(filterVal);
                this._model._actionGroupEntity.setDeviceFilter(this._filterValue);
            }
        } else {
            this._filterValue = null;
            this._model._actionGroupEntity.setDeviceFilter(this._filterValue);
        }
        this._compareFilterValues();
    }

    _onCancelClick() {
        this._model.closePopUp();
    }

    _submitAction() {
        Promise.all([])
            .then(() => {
                if (!this._model._isSeeded || !this._model._isInherited) {
                    if (!this._filterValue || !(this._filterValue.$and || this._filterValue.$or)) {
                        this._model._actionGroupEntity.setDeviceFilter(this._filterValue);
                        return Promise.resolve();
                    }
                    if (this._advancedFilter._renderer.$builderContainer.queryBuilder("validate")) {
                        this._model._actionGroupEntity.setDeviceFilter(
                            this._advancedFilter._renderer.$builderContainer.queryBuilder(
                                "getMongo"
                            )
                        );
                        return Promise.resolve();
                    }

                    return Promise.reject("Save Failed::Error in Advanced Filter");
                }
            })
            .then(() => {
                this._model
                    .saveActionGroup()
                    .then(() => {
                        this.model.closePopUp();
                    })
                    .catch((err) => {
                        this.model.userFeedback.displayError(err);
                        console.log("Save Failed::", err);
                    });
            })
            .catch((err) => {
                this.model.userFeedback.displayError(err);
                console.log(err);
            });
    }

    //org vis handlers
    _buildOrgVisibilitylList(orgVisibilityLevel) {
        let orgVisOptions;
        switch (orgVisibilityLevel) {
            case "ALL":
                orgVisOptions = [
                    {label: "Some", value: "SOME"},
                    {label: "All", value: "ALL"},
                ];
                break;
            case "SOME":
                orgVisOptions = [
                    {label: "Some", value: "SOME"},
                    {label: "All", value: "ALL", disabled: true},
                ];
                break;
        }
        return orgVisOptions;
    }

    _clickHandler(e) {
        this._saveAndCloseEditCellAction(e);
    }

    /*******************************************/
    /* END HANDLER METHODS */
    /* START PRIVATE API METHODS */
    /*******************************************/
    _compareFilterValues() {
        const filterValuesMatch = this._doFiltersMatch();
        this._setFilterMatchContainerHeight();
        if (filterValuesMatch) {
            this._filterMatchContainer.innerHTML = "";
            this._matchIconFilter.dataset.icon = "application.check_sign";
            this._filterMatchContainer.appendChild(this._matchIconFilter);
        } else {
            this._filterMatchContainer.innerHTML = "";
            this._matchIconFilter.dataset.icon = "application.cross_mark";
            this._filterMatchContainer.appendChild(this._matchIconFilter);
        }
    }
    _setFilterMatchContainerHeight() {
        const filterHeight = this._advancedFilter._renderer._rootElem.clientHeight;
        const readOnlyFilterHeight = this._advancedFilterReadonly._renderer._rootElem.clientHeight;
        this._filterMatchParent.style.top = `${filterHeight / 2}px`;
        this._filterMatchParent.style.bottom = `${readOnlyFilterHeight / 2}px`;
        this._filterMatchContainer.style.top = `${filterHeight / 2 + 10}px`;
    }

    enableDisableSubmitButton(state) {
        this._confirmButton.setDisabled(state);
    }

    _doFiltersMatch() {
        // Changing the filter values to null if they are falsy.
        this._filterValue =
            !this._filterValue || !(this._filterValue.$and || this._filterValue.$or)
                ? null
                : this._filterValue;
        this._readOnlyFilterValue =
            !this._readOnlyFilterValue ||
            !(this._readOnlyFilterValue.$and || this._readOnlyFilterValue.$or)
                ? null
                : this._readOnlyFilterValue;
        return JSON.stringify(this._filterValue) === JSON.stringify(this._readOnlyFilterValue);
    }

    _setDate(newVal) {
        if (newVal) {
            return new Date(newVal).toLocaleDateString(undefined, {
                timeZone: "UTC",
                year: "numeric",
                month: "2-digit",
                day: "2-digit",
                hour: "2-digit",
                minute: "2-digit",
            });
        } else {
            return "";
        }
    }

    _setError(error) {
        let errorStr = "Error";
        if (typeof error === "string") {
            errorStr = error;
        } else if (error instanceof Error) {
            errorStr = error.toString();
        } else if (error && error.errorMsg) {
            const errorCode = error.errorCode || "Error";
            const errorMsg = error.errorMsg;
            errorStr = `${errorCode}: ${errorMsg}`;
        }
        this._errorContainer.innerText = errorStr;
    }

    _clearError() {
        this._errorContainer.innerText = "";
    }

    setSlickGridTableData(newData) {
        if (newData) {
            return new Promise((resolve) => {
                this._permissionsSTDataVIew.setItems(newData.permissionsData);
                if (this._model._compareActionGroup) {
                    this._permissionsST.setColumns(this._columns);
                    this._emailST.setColumns(this._emailColumns);
                } else {
                    this._permissionsST.setColumns(this._noCompareColumns);
                    this._emailST.setColumns(this._noCompareEmailColumns);
                }
                this._permissionsST.invalidate();
                this._permissionsST.render();

                this._emailSTDataVIew.setItems(newData.emailData);
                this._emailST.invalidate();
                this._emailST.render();

                this._fitTableSize(); // fix windows os slickgrid resize issue
                return resolve();
            });
        }
    }
    _fitTableSize() {
        if (this._permissionsST) {
            this._permissionsST.resizeCanvas();
        }
        if (this._emailST) {
            this._emailST.resizeCanvas();
        }
    }

    _setTitleReadOnly() {
        const vp = this._permissionsST.getViewportNode();
        this._model._categories.forEach((cat) => {
            const permNodes = vp.querySelectorAll(`.${cat}:not(.title-row)`);
            const titleNode = vp.querySelector("." + cat + ".title-row");
            let isReadOnly = true;
            permNodes.forEach((node) => {
                if (!node.classList.contains("readonly-row")) {
                    isReadOnly = false;
                }
            });
            if (isReadOnly) {
                titleNode.classList.add("readonly-row");
            }
        });
    }

    _createFilterEmptyElement(roleName) {
        const containerEl = document.createElement("div");
        containerEl.innerText = `No filter defined for ${roleName}`;
        return containerEl;
    }

    _saveAndCloseEditCellAction(e) {
        //do nothing if no active editors
        if (
            !this._permissionsST.getEditorLock().isActive() &&
            !this._emailST.getEditorLock().isActive()
        ) {
            return false;
        }

        //get event bubbling path
        const path = e.path || e.composedPath();

        //check if click was outside of current editing cell
        let outsideClick = true;
        _.each(path, (element) => {
            const elHasClass = (className) =>
                element.classList && element.classList.contains(className);

            if (elHasClass("slick-cell")) {
                const cellHasActiveEditor = elHasClass("active") && elHasClass("editable");
                outsideClick = !cellHasActiveEditor;
                return false;
            }

            if (element === this._permissionSTContainer || element === this._emailSTContainer) {
                return false;
            }
        });

        if (outsideClick) {
            this._permissionsST.getEditorLock().commitCurrentEdit();
            this._emailST.getEditorLock().commitCurrentEdit();
        }
    }

    /*******************************************/
    /*  END PRIVATE API METHODS */
    /*******************************************/
}

module.exports = AddEditUserRoleRenderer;
